import moment from "moment-timezone";

/*
 * Date-range arithmetic for the inventory forecast.
 *
 * Kept pure and separate from the panel so the rules the desk cares about —
 * what "today" means at this hotel, how far ahead they may look — are testable
 * without rendering a chart. Every date is a venue-local 'YYYY-MM-DD' string,
 * because the API buckets by venue-local calendar day; a console sitting in a
 * different timezone to the hotel must still ask about the hotel's days.
 */

export const DATE_FORMAT = "YYYY-MM-DD";

/** Default window: today plus the next six days. One week of desk planning. */
export const DEFAULT_RANGE_DAYS = 7;

/*
 * The API refuses anything longer, matching mapBookingsCount's maxRangeLength,
 * so clamp here rather than letting the request bounce.
 */
export const MAX_RANGE_DAYS = 90;

/** How far back the forecast may look. Past days are history, not a forecast. */
export const MAX_PAST_DAYS = 30;

/** Today as a venue-local date string. */
export function today(timezone) {
  return (timezone ? moment().tz(timezone) : moment()).format(DATE_FORMAT);
}

export function addDays(date, days) {
  return moment(date, DATE_FORMAT).add(days, "days").format(DATE_FORMAT);
}

/** Inclusive day count between two date strings. */
export function spanDays(startDate, endDate) {
  return moment(endDate, DATE_FORMAT).diff(moment(startDate, DATE_FORMAT), "days") + 1;
}

/** A range of `days` starting today. */
export function defaultRange(timezone, days = DEFAULT_RANGE_DAYS) {
  const startDate = today(timezone);
  return { startDate, endDate: addDays(startDate, days - 1) };
}

export function isValidDate(date) {
  return moment(date, DATE_FORMAT, true).isValid();
}

/**
 * Bring a user-entered range back inside what the API will answer.
 *
 * The edited end of the range is the one that moves: nudging the start date
 * past the end date should push the end date along rather than silently
 * rewriting what the user just typed.
 */
export function clampRange({ startDate, endDate, timezone, edited = "endDate" }) {
  if (!isValidDate(startDate) || !isValidDate(endDate)) {
    return { ...defaultRange(timezone), clamped: true };
  }

  const earliest = addDays(today(timezone), -MAX_PAST_DAYS);
  let start = startDate < earliest ? earliest : startDate;
  let end = endDate < earliest ? earliest : endDate;
  let clamped = start !== startDate || end !== endDate;

  if (end < start) {
    if (edited === "startDate") {
      end = start;
    } else {
      start = end;
    }
    clamped = true;
  }

  if (spanDays(start, end) > MAX_RANGE_DAYS) {
    if (edited === "startDate") {
      start = addDays(end, -(MAX_RANGE_DAYS - 1));
    } else {
      end = addDays(start, MAX_RANGE_DAYS - 1);
    }
    clamped = true;
  }

  return { startDate: start, endDate: end, clamped };
}

/**
 * Milliseconds until the next venue-local midnight.
 *
 * The panel can sit open across a shift change, and a window the desk anchored
 * to "today" should not quietly become yesterday's when the date rolls over -
 * least of all for the overnight desk, which is the shift most likely to still
 * have it open.
 */
export function msUntilNextVenueDay(timezone) {
  const now = timezone ? moment().tz(timezone) : moment();
  return now.clone().add(1, "day").startOf("day").diff(now);
}

/** Short label for an axis tick or a busy-day tile: "Fri 5 Sep". */
export function formatDayShort(date) {
  return moment(date, DATE_FORMAT).format("ddd D MMM");
}

/** Long label for the drilldown header: "Friday 5 September". */
export function formatDayLong(date) {
  return moment(date, DATE_FORMAT).format("dddd D MMMM");
}

/** Axis label without the weekday: "5 Sep". */
export function formatDayAxis(date) {
  return moment(date, DATE_FORMAT).format("D MMM");
}

/** Venue-local clock time of a movement instant: "14:35". */
export function formatMovementTime(instant, timezone) {
  const m = timezone ? moment(instant).tz(timezone) : moment(instant);
  return m.format("HH:mm");
}
